import { Fade, LinearProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useState } from "react";
import EventManager from "../../overlay/EventManager/EventManager";
import { FRAME_EVENT } from "../../overlay/consts/Frame";

function easeInOutQuad(x: number): number {
    return x === 0
        ? 0
        : x === 1
          ? 1
          : x < 0.5
            ? Math.pow(2, 20 * x - 10) / 2
            : (2 - Math.pow(2, -20 * x + 10)) / 2;
}

const ProgressContainer = styled("div")<{ isVisible: boolean }>(
    ({ isVisible }) => ({
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 999999,
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? "visible" : "hidden",
        transition: "opacity 0.5s ease-out, visibility 0.5s ease-out",
    }),
);

const StyledLinearProgress = styled(LinearProgress)({
    backgroundColor: "transparent",
    height: "2px",
    "& .MuiLinearProgress-bar": {
        transition: "transform 0.016s cubic-bezier(0.4, 0, 0.2, 1)",
    },
});

interface LoaderState {
    isVisible: boolean;
    progress: number;
    expectedTime: number;
    pendingRequests: number;
    startTime: number;
}

const INITIAL_STATE: LoaderState = {
    isVisible: false,
    progress: 0,
    expectedTime: 3000,
    pendingRequests: 0,
    startTime: 0,
};

const calculateProgressTime = (x: number): number => {
    const base = 2000; // Base value
    const leastIncrement = 100; // Initial increment

    if (x < 1) return 0; // Handle invalid values

    let result = base;
    let increment = base;

    for (let i = 2; i <= x; i++) {
        increment = Math.max(increment / 2, leastIncrement); // Reduce increment by half
        result += increment; // Add increment to result
    }

    return result;
};

const eventManager = EventManager.getInstance();

export const ProgressBarManager: React.FC = () => {
    const [state, setState] = useState<LoaderState>(INITIAL_STATE);
    const [progressTimer, setProgressTimer] = useState<NodeJS.Timeout | null>(
        null,
    );

    const startProgress = useCallback(() => {
        const expectedTime = calculateProgressTime(state.pendingRequests);
        setState((prev) => ({
            ...prev,
            isVisible: true,
            startTime: prev.startTime ? prev.startTime : Date.now(),
            // Never decrease progress, only increase
            expectedTime: Math.max(prev.expectedTime, expectedTime),
        }));

        if (progressTimer) {
            clearInterval(progressTimer);
        }
        const timer = setInterval(() => {
            setState((prev) => {
                const elapsedTime = Date.now() - prev.startTime;
                const rawProgress = elapsedTime / prev.expectedTime;

                // Apply easing function
                const easedProgress = easeInOutQuad(Math.min(rawProgress, 1));
                // Never go below current progress
                const newProgress = Math.max(
                    prev.progress,
                    Math.min(easedProgress * 80, 80),
                );

                if (newProgress >= 80 && newProgress < 95) {
                    return { ...prev, progress: prev.progress + 0.04 };
                }
                if (newProgress >= 95) {
                    clearInterval(timer);
                    return { ...prev, progress: 95 };
                }

                return { ...prev, progress: newProgress };
            });
        }, 16);

        setProgressTimer(timer);
    }, [progressTimer, state.pendingRequests]);

    const completeProgress = useCallback(() => {
        if (progressTimer) {
            clearInterval(progressTimer);
            setProgressTimer(null);
            setState((prev) => ({ ...prev, pendingRequests: 0, startTime: 0 }));
        }

        // First set progress to 100%
        setState((prev) => ({ ...prev, progress: 100 }));

        // Wait for fade out animation before resetting state
        setTimeout(() => {
            setState((prev) => ({ ...prev, isVisible: false }));
            // Reset after fade out
            setState(INITIAL_STATE);
        }, 200);
    }, [progressTimer]);

    useEffect(() => {
        const handleMessage = (type: string, data: any) => {
            switch (type) {
                case FRAME_EVENT.START_PROGRESS_BAR:
                    startProgress();
                    break;
                case FRAME_EVENT.UPDATE_PROGRESS_BAR:
                    setState((prev) => ({
                        ...prev,
                        pendingRequests:
                            data?.requestCount + prev.pendingRequests,
                    }));
                    break;
                case FRAME_EVENT.STOP_PROGRESS_BAR:
                    completeProgress();
                    break;
            }
        };

        const subscription = eventManager.subscribe(handleMessage);
        return () => {
            subscription?.();
            if (progressTimer) {
                clearInterval(progressTimer);
            }
        };
    }, [startProgress, completeProgress, progressTimer]);

    if (!state.isVisible) return null;

    return (
        <Fade in={state.isVisible} timeout={500}>
            <ProgressContainer
                isVisible={state.isVisible}
                data-testid="progress-bar-manager"
            >
                <StyledLinearProgress
                    variant="determinate"
                    value={state.progress}
                />
            </ProgressContainer>
        </Fade>
    );
};

export default ProgressBarManager;
